/**
 * Init compartido de Sentry para las 3 SPAs.
 *
 * Diseño:
 *   - Lee `VITE_PUBLIC_SENTRY_DSN` en runtime. Si NO está set, no-op + log de
 *     dev. No se rompe la app si Sentry no está configurado todavía.
 *   - El SDK no agrega bundle si no se importa: por eso `await import()` lazy.
 *     Esto le permite a la app correr aunque @sentry/react no resuelva.
 *   - Tracing: 10% de transactions en prod, 100% en dev (cap por costo).
 *   - Sample rate de errores: 100% (queremos ver todo).
 *   - PII scrubbing: el SDK por default NO manda `req.headers.authorization`,
 *     `cookies`, `password`. Reforzamos con `beforeSend` para garantizar.
 *   - Tag `app: landing|dashboard|equipo` para filtrar en el dashboard Sentry.
 *
 * Seguridad:
 *   - El DSN `VITE_PUBLIC_*` viaja al cliente — ES PUBLIC POR DISEÑO
 *     (controlado por Sentry: el DSN solo permite mandar errores, no leerlos).
 *   - Errores con PII en el mensaje se filtran antes de enviar.
 *
 * Uso (en main.tsx de cada SPA, ANTES de createRoot):
 *   import { initSentry } from '@aosenuma/lib-supabase/helpers/sentry';
 *   await initSentry({ app: 'dashboard' });
 */

interface InitSentryOptions {
  /** Identifica la SPA en los tags de Sentry para filtros. */
  app: 'landing' | 'dashboard' | 'equipo';
  /** Override del sample rate de tracing (default 0.1 prod, 1.0 dev). */
  tracesSampleRate?: number;
  /** Custom release/version (default: leer de import.meta.env.VITE_APP_VERSION). */
  release?: string;
}

/** Patterns para scrub de errores con PII potencial. */
const PII_PATTERNS = [
  /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, // emails
  /\b\+?52\s?\d{10}\b/g,                          // teléfonos MX
  /\b\d{4}\s?\d{4}\s?\d{4}\s?\d{4}\b/g,          // tarjetas
  /\bCURP[A-Z0-9]*\b/gi,                          // CURP
];

function scrubPII(text: string | undefined): string | undefined {
  if (!text) return text;
  let out = text;
  for (const re of PII_PATTERNS) out = out.replace(re, '[REDACTED]');
  return out;
}

export async function initSentry(opts: InitSentryOptions): Promise<void> {
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  const dsn = env.VITE_PUBLIC_SENTRY_DSN?.trim();

  if (!dsn) {
    if (env.DEV) {
      // eslint-disable-next-line no-console
      console.info(
        `[sentry] skip · falta VITE_PUBLIC_SENTRY_DSN (app=${opts.app}). ` +
          `Crear proyecto en sentry.io y pegar DSN en .env.local para activar.`,
      );
    }
    return;
  }

  // Lazy import: si por alguna razón el paquete no resuelve, la app no se cae.
  let Sentry: typeof import('@sentry/react');
  try {
    Sentry = await import('@sentry/react');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] no se pudo cargar @sentry/react', e);
    return;
  }

  const isDev = !!env.DEV;
  const release = opts.release ?? env.VITE_APP_VERSION ?? 'dev';
  const environment = env.VITE_PUBLIC_APP_ENV ?? (isDev ? 'development' : 'production');

  Sentry.init({
    dsn,
    environment,
    release: `aosenuma-${opts.app}@${release}`,
    integrations: [
      // Tracing solo en producción. En dev:
      //  - browserTracingIntegration manda envelopes constantes que fallan
      //    en Safari background-tab suspension → ruido de errores y consume
      //    Sentry quota innecesariamente.
      //  - Errors capturados (Sentry.captureException) siguen funcionando
      //    sin tracing.
      ...(isDev ? [] : [Sentry.browserTracingIntegration()]),
      // Replay solo en prod (en dev consume mucho ancho de banda).
      ...(isDev
        ? []
        : [
            Sentry.replayIntegration({
              maskAllText: true,    // PII safety: enmascara todo texto
              maskAllInputs: true,
              blockAllMedia: true,  // imágenes pueden ser fotos de INE
            }),
          ]),
    ],
    tracesSampleRate: opts.tracesSampleRate ?? (isDev ? 0 : 0.1),
    replaysSessionSampleRate: 0,  // No grabar sesiones happy-path (cuestan)
    replaysOnErrorSampleRate: 1.0, // Solo grabar cuando hay error
    sendDefaultPii: false,         // No mandar IP, headers cookies, etc.
    initialScope: { tags: { app: opts.app } },
    beforeSend(event) {
      // Defensa en profundidad: scrub message + exception values.
      if (event.message) event.message = scrubPII(event.message)!;
      if (event.exception?.values) {
        for (const v of event.exception.values) {
          if (v.value) v.value = scrubPII(v.value);
        }
      }
      // Quitar request body — puede contener PII de captura.
      if (event.request?.data) delete event.request.data;
      return event;
    },
  });

  if (isDev) {
    // eslint-disable-next-line no-console
    console.info(`[sentry] init OK · app=${opts.app} · env=${environment}`);
  }
}

/** Helper para reportar un error manualmente desde código de aplicación. */
export async function captureError(
  err: unknown,
  context?: Record<string, unknown>,
): Promise<void> {
  try {
    const Sentry = await import('@sentry/react');
    Sentry.captureException(err, { extra: context });
  } catch {
    // Sentry no cargó — al menos logueamos.
    // eslint-disable-next-line no-console
    console.error('[captureError]', err, context);
  }
}
